import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Button } from '../../../components/ui/button.tsx';
import { Input } from '../../../components/ui/input.tsx';
import { Label } from '../../../components/ui/label.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/ui/dialog.tsx';
import { LoadingSpinner } from '../../../components/ui/loading-spinner.tsx';
import { Check, Trash2, Plus, AlertTriangle } from 'lucide-react';

import {
  useCreateRecordType,
  useDeleteRecordTypebyId,
  useGetAllRecordTypes,
  useUpdateRecordTypebyId,
} from '../../../api/recordTypes/recordTypesQueries.js';

const colors = [
  'bg-purple-600',
  'bg-purple-500',
  'bg-purple-400',
  'bg-violet-500',
  'bg-indigo-500',
];

export default function RecordsManagement() {
  const [assessments, setAssessments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [deleteValues, setDeleteValues] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [newRecord, setNewRecord] = useState({ name: '', percentage: 0 });

  const { data: buckets, isLoading } = useGetAllRecordTypes();
  const {
    mutate: createRecordType,
    isPending,
    isError,
  } = useCreateRecordType();
  const { mutate: updateRecordType, isPending: isUpdatePending } =
    useUpdateRecordTypebyId();
  const { mutate: deleteRecordType, isPending: isdeletePending } =
    useDeleteRecordTypebyId();

  useEffect(() => {
    if (!isLoading && !isError) {
      const recordBuckets = [];
      for (const recordType of buckets.data) {
        const newAssessment = {
          id: recordType.id,
          name: recordType.name,
          percentage: recordType.totalMarksObtainable,
          color: colors[recordBuckets.length % colors.length],
        };
        recordBuckets.push(newAssessment);
      }
      setAssessments(recordBuckets);
    }
  }, [buckets, isLoading, isError]);

  const totalPercentage = assessments.reduce(
    (sum, assessment) => sum + assessment.percentage,
    0
  );

  const handleEdit = (id, currentPercentage, name) => {
    setEditingId(id);
    setEditValues({ ...editValues, [id]: { weight: currentPercentage, name } });
  };

  const handleSave = (id) => {
    const newRecordValues = editValues[id];
    updateRecordType([
      id,
      {
        name: newRecordValues.name,
        weight: newRecordValues.weight,
      },
    ]);
    setEditingId(null);
  };

  const handleDelete = (id, currentPercentage, name) => {
    // deleteRecordType(id);
    setDeletingId(id);
    setDeleteValues({
      ...deleteValues,
      [id]: { weight: currentPercentage, name },
    });
    // setAssessments(assessments.filter((assessment) => assessment.id !== id));
  };

  const handleCreateRecord = () => {
    if (newRecord.name.trim() && newRecord.percentage > 0) {
      if (totalPercentage + newRecord.percentage > 100) {
        toast.error('Percentage cannot add up to more than 100% total');
      } else {
        createRecordType({
          name: newRecord.name,
          weight: newRecord.percentage,
        });
        setNewRecord({ name: '', percentage: 0 });
        setIsModalOpen(false);
      }
    }
  };

  return (
    <div className="rounded-lg p-6 bg-white">
      {/* Header Section */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">
            Records Management
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Available Obtainable Score
          </p>

          {isLoading ? (
            <div className="flex place-content-center">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden flex">
                {assessments.map((assessment, index) => (
                  <div
                    key={assessment.id}
                    className={
                      assessment.color + ' h-full has-tooltip cursor-pointer'
                    }
                    style={{ width: `${(assessment.percentage / 100) * 100}%` }}
                  >
                    <span className="tooltip rounded shadow-lg p-1 bg-gray-100 text-black -mt-8">
                      {assessment.name}
                    </span>
                  </div>
                ))}
              </div>
              <span className="text-sm font-medium text-gray-700">
                {totalPercentage}%
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Assessment Rows */}
      <div className="space-y-4 mb-6">
        {isLoading ? (
          <div className="flex place-content-center">
            <LoadingSpinner />
          </div>
        ) : (
          <div>
            {assessments.map((assessment) => (
              <div
                key={assessment.id}
                className="flex items-center justify-between py-3"
              >
                <div className="flex items-center gap-4 flex-1">
                  {editingId === assessment.id ? (
                    <>
                      {assessment.name === 'Attendance' ? (
                        <div className="flex items-center gap-3">
                          <span className="text-purple-600 font-medium">
                            {assessment.name}
                          </span>
                          {assessment.name === 'Attendance' && (
                            <span className="text-sm text-gray-500">
                              Default Record Type
                            </span>
                          )}
                        </div>
                      ) : (
                        <Input
                          type="text"
                          value={editValues[assessment.id].name}
                          onChange={(e) =>
                            setEditValues({
                              ...editValues,
                              [assessment.id]: {
                                ...editValues[assessment.id],
                                name: e.target.value || '',
                              },
                            })
                          }
                          className="w-32 h-8 text-sm text-center"
                        />
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-3">
                      <span className="text-purple-600 font-medium">
                        {assessment.name}
                      </span>
                      {assessment.name === 'Attendance' && (
                        <span className="text-sm text-gray-500">
                          Default Record Type
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {editingId === assessment.id ? (
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={editValues[assessment.id].weight}
                        onChange={(e) =>
                          setEditValues({
                            ...editValues,
                            [assessment.id]: {
                              ...editValues[assessment.id],
                              weight: Number.parseInt(e.target.value) || 0,
                            },
                          })
                        }
                        className="w-16 h-8 text-sm text-center"
                      />
                    ) : (
                      <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-md text-sm font-medium">
                        {assessment.percentage}
                      </span>
                    )}
                    <span className="text-gray-500 text-sm">%</span>
                  </div>

                  <div className="flex items-center gap-2">
                    {editingId === assessment.id ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => handleSave(assessment.id)}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        onClick={() =>
                          handleEdit(
                            assessment.id,
                            assessment.percentage,
                            assessment.name
                          )
                        }
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    {assessment.name !== 'Attendance' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        // onClick={() => handleDelete(assessment.id)}
                        onClick={() => {
                          handleDelete(
                            assessment.id,
                            assessment.percentage,
                            assessment.name
                          );
                          setIsDeleteModalOpen(!isDeleteModalOpen);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : (
                      <div className="h-8 w-8" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="border-purple-300 text-purple-600 hover:bg-purple-50 hover:border-purple-400 flex items-center gap-2 bg-transparent"
          >
            <Plus className="h-4 w-4" />
            Create New Record
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Record</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="record-name">Record Name</Label>
              <Input
                id="record-name"
                placeholder="Enter record name"
                value={newRecord.name}
                onChange={(e) =>
                  setNewRecord({ ...newRecord, name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="record-weight">Weight (%)</Label>
              <Input
                id="record-weight"
                type="number"
                min="0"
                max="100"
                placeholder="Enter percentage"
                value={newRecord.percentage || ''}
                onChange={(e) =>
                  setNewRecord({
                    ...newRecord,
                    percentage: Number.parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateRecord}
              className="bg-purple-600 hover:bg-purple-700"
              disabled={
                !newRecord.name.trim() || newRecord.percentage <= 0 || isPending
              }
            >
              {isPending ? 'Loading...' : 'Create Record'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Record Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Record
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this record? You'll lose access to
              all records stored under this record bucket.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 p-4 rounded-lg">
            <p className="text-sm text-red-800">
              <strong>What happens when you delete this:</strong>
            </p>
            <ul className="mt-2 text-sm text-red-700 space-y-1">
              <li>
                • You will lose all associated grades with this record type
              </li>
              <li>• You will orphan student records in the database</li>
              <li>• Your data will not be preserved</li>
              <li>• You cannot undo this step</li>
            </ul>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Keep Record Type
            </Button>
            <Button
              onClick={() => {
                setIsDeleteModalOpen(false);
                deleteRecordType(deletingId);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, I am aware! Delete Record Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
